import { useSelector } from '@xstate/react';
import React, { useCallback, useContext, FocusEventHandler } from 'react';
import {
  BlurEvent,
  ChangeEventHandler,
  FormContext,
  FormField,
  FormInterpreter,
  FormState,
  SetEvent,
} from '../../../state-machines/form-machine';
import { WizardContext, IWizardContext } from '../wizard-context';
import {
  WizardContext as WizardMachineContext,
  WizardState,
} from '../../../state-machines/wizard-machine';
import { WizardInterpreter } from '../../../components/Wizard';
import {
  currentStepIndexSelector,
  currentFormSelector,
  stepsSelector,
  validatorsSelector,
  transformersSelector,
} from '../selectors';
import { get, isEqual, isEqualWith } from 'lodash';
export interface FieldHandler extends FormField {
  onChange?: ChangeEventHandler;
  onBlur?: FocusEventHandler;
  set: (field: Omit<FormField, 'name'>) => void;
}

export const getWizardFieldValue = (
  state: WizardState,
  fieldName: string
): FieldHandler | undefined => {
  if (!fieldName) return undefined;
  if (!state.context.steps) return undefined;
  for (const step of state.context.steps) {
    const snap = step.getSnapshot();
    const [field, isExternal] = getField(fieldName, {
      fields:
        snap?.context.fields.map((f) => ({ ...f, formName: step.id })) ?? [],
      externalFields: state.context.globalFields,
    });
    if (field)
      return getFieldHandler(
        field,
        { send: (step as FormInterpreter).send },
        isExternal
      );
  }
  return undefined;
};

const parseFieldName = (fullFieldName: string) => {
  const split = fullFieldName.split('.');
  const containsFormName = split.length > 1;
  const formName = containsFormName ? split[0] : undefined;
  const fieldName = containsFormName ? split.slice(1).join('.') : fullFieldName;
  return [fieldName, formName];
};

const findField =
  ({ field }: { field: FormField }) =>
  (comparedField: FormField) => {
    const [comparedFieldName, comparedFieldFormName] = parseFieldName(
      comparedField.name
    );
    const fieldNameMatches = field.name === comparedFieldName;
    if (field.formName === '*' || !field.formName) return fieldNameMatches;
    return (
      fieldNameMatches &&
      field.formName === (comparedFieldFormName ?? comparedField.formName)
    );
  };

const getField = (
  fullFieldName: string,
  {
    fields,
    externalFields,
  }: Pick<FormState['context'], 'fields' | 'externalFields'> = {
    fields: [],
    externalFields: [],
  }
) => {
  const [fieldName = '', formName] = parseFieldName(fullFieldName);
  let isExternal = false;
  let field;
  // TODO: refactor the code in order not to explicitely set formName to ''
  // in case the same fieldName has been used on different steps
  field = fields.find(
    findField({
      field: { name: fieldName, formName },
    })
  );
  if (!field) {
    isExternal = true;
    field = (externalFields ?? []).find(
      findField({
        field: { name: fieldName, formName },
      })
    );
  }
  return [field, isExternal];
};

const getFieldHandler = (
  field: FormField,
  { send }: Pick<FormInterpreter, 'send'>,
  isExternal?: boolean
) => {
  if (!field) return undefined;
  const onChange: ChangeEventHandler = (e) => {
    if (e.persist && typeof e.persist === 'function') e.persist();
    send({
      type: 'CHANGE',
      target: { name: e.target.name, value: e.target.value },
    });
  };
  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.persist && typeof e.persist === 'function') e.persist();
    send({
      type: e.type,
      target: { name: e.target.name, value: e.target.value },
    } as BlurEvent);
  };
  const set = (formField: Omit<FormField, 'name'>) => {
    send({
      type: 'SET',
      [isExternal ? 'externalFields' : 'fields']: [
        { name: field.name, ...formField },
      ],
      addField: false,
    } as SetEvent);
  };
  return {
    ...field,
    onChange,
    onBlur,
    set,
    value: field.value,
    errors: field.errors,
  };
};

export const getFormFieldValue = (
  state: FormState,
  fieldName: string,
  service: FormInterpreter
): FieldHandler | undefined => {
  const [field, isExternal] = getField(fieldName, state.context);
  return getFieldHandler(field, service, isExternal);
};

const sortAlphabetically = (a: string, b: string) => {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

const getFormFromNameOrIndex = (
  nameOrIndex: string | number,
  steps: WizardMachineContext['steps']
) => {
  if (!steps) return;
  return typeof nameOrIndex === 'number'
    ? steps[nameOrIndex]
    : steps.find((step) => {
        return step.getSnapshot()?.context.name === nameOrIndex;
      });
};

export const useFieldsAttribute: <K extends keyof FormField>(
  attributeName: K,
  formNameOrIndex: string | number
) => FormField[K][] | undefined = (attributeName, formNameOrIndex) => {
  const selector = useCallback(
    (state: WizardState) => {
      if (!state.context.steps) return undefined;
      const form = getFormFromNameOrIndex(formNameOrIndex, state.context.steps);
      if (!form || !form.getSnapshot()) return undefined;
      const fields = form.getSnapshot()?.context.fields ?? [];
      const sortedFields = [...fields].sort((f1, f2) =>
        sortAlphabetically(f1.name, f2.name)
      );
      return sortedFields.map((f) => f[attributeName]);
    },
    [attributeName, formNameOrIndex]
  );
  const { wizardService } = useContext<IWizardContext>(WizardContext);
  if (!wizardService) return;
  return useSelector(wizardService, selector, (prev, next) => {
    return (prev ?? []).every((attr, i) => {
      if (!next || i >= next.length) return false;
      return attr === next[i];
    });
  });
};

export const useFormAttribute: <K extends keyof FormContext>(
  attributeName: K,
  formNameOrIndex: string | number
) => FormContext[K] | undefined = (attributeName, formNameOrIndex) => {
  const selector = useCallback(
    (state: WizardState) => {
      if (!state.context.steps) return undefined;
      const form =
        typeof formNameOrIndex === 'number'
          ? state.context.steps[formNameOrIndex]
          : state.context.steps.find((step) => {
              return step.getSnapshot()?.context.name === formNameOrIndex;
            });
      if (!form || !form.getSnapshot()) return undefined;
      return form.getSnapshot()?.context[attributeName];
    },
    [attributeName, formNameOrIndex]
  );
  const { wizardService } = useContext<IWizardContext>(WizardContext);
  if (!wizardService) return;
  return useSelector(wizardService, selector);
};

type AnyFormInterpreter = WizardInterpreter | FormInterpreter;

export const getDefaultService = <I extends AnyFormInterpreter>(
  service?: I
) => {
  if (service) return service;
  return useWizardService();
};

const fieldSelectorCallback = (
  state: FormState | WizardState,
  fieldName: string,
  service?: AnyFormInterpreter
) => {
  if (state.context.hasOwnProperty('steps'))
    return getWizardFieldValue(state as WizardState, fieldName);
  else
    return getFormFieldValue(
      state as FormState,
      fieldName,
      service as FormInterpreter
    );
};

const fieldAttributesToIgnore = ['onChange', 'onBlur', 'set'];

export const useField: (
  fieldName: string,
  service?: AnyFormInterpreter
) => FieldHandler | undefined = (fieldName, service) => {
  const selector = useCallback(
    (state: FormState | WizardState) => {
      return fieldSelectorCallback(state, fieldName, service);
    },
    [fieldName]
  );
  const defaultService = getDefaultService(service);
  if (!defaultService) return undefined;
  return useSelector(defaultService, selector, (prev, next) => {
    let shouldSkip = true;
    const prevObj = prev === undefined ? {} : prev;
    const nextObj = next === undefined ? {} : next;
    for (const key in prevObj) {
      if (!fieldAttributesToIgnore.includes(key)) {
        shouldSkip = Object.is(prevObj[key], nextObj[key]);
        if (!shouldSkip) return shouldSkip;
      }
    }
    return shouldSkip;
  });
};

type NullableFieldHandler = FieldHandler | undefined;

const fieldsHandlerFilter = (
  previousFields: NullableFieldHandler[],
  currentFields: NullableFieldHandler[]
) => {
  let shouldSendOldValue = previousFields.length === currentFields.length;
  if (!shouldSendOldValue) return false;
  shouldSendOldValue = previousFields.reduce((res, oldField, i) => {
    return !res
      ? res
      : isEqualWith(oldField, currentFields[i], (a = {}, b = {}) => {
          const objectWithMoreKeys =
            Object.keys(a).length > Object.keys(b).length ? a : b;
          const attributesToCheck = Object.keys(objectWithMoreKeys).filter(
            (key) => !fieldAttributesToIgnore.includes(key)
          );
          let oldObjectIsEqualToNew = true;
          for (const attr of attributesToCheck) {
            oldObjectIsEqualToNew = isEqual(a[attr], b[attr]);
            if (!oldObjectIsEqualToNew) return false;
          }
          return oldObjectIsEqualToNew;
        });
  }, shouldSendOldValue as boolean);
  return shouldSendOldValue;
};

export const useFields: (
  fieldNames: string[],
  service?: AnyFormInterpreter
) => (FieldHandler | undefined)[] = (fieldNames, service) => {
  const selector = useCallback(
    (state) => {
      return fieldNames.map((fieldName) => {
        return fieldSelectorCallback(state, fieldName, service);
      });
    },
    [fieldNames]
  );
  const defaultService = getDefaultService(service);
  if (!defaultService) return [];
  return useSelector(defaultService, selector, fieldsHandlerFilter);
};

/**
 * @deprecated
 * do the following instead:
 * const wizardStepIndex = useCurrentStepIndex();
 * const { send } = useForm(wizardStepIndex);
 */
export const useCurrentForm = (): [
  FormState | undefined,
  WizardInterpreter['send'] | undefined
] => {
  const { wizardService } = useContext<IWizardContext>(WizardContext);
  if (!wizardService) return [undefined, undefined];
  const currentForm = useSelector(wizardService, currentFormSelector);
  return [currentForm, wizardService.send];
};

type FormObject = { state: FormState; send: WizardInterpreter['send'] };

export const useForms = (
  namesOrIndexes: string[] | number[],
  service?: WizardInterpreter
): FormObject[] | undefined => {
  const selector = useCallback(
    (state) => {
      const steps = state.context.steps;
      const isNameBased =
        namesOrIndexes.length > 0 && typeof namesOrIndexes[0] === 'string';
      if (isNameBased) {
        return steps
          .filter((s) => (namesOrIndexes as string[]).includes(s.id))
          .map((s) => ({ state: s.getSnapshot(), send: s.send }));
      } else {
        const forms: FormObject[] = [];
        for (const index of namesOrIndexes as number[]) {
          if (index < steps.length)
            forms.push({
              state: steps[index].getSnapshot(),
              send: steps[index].send,
            });
          else return undefined;
        }
        return forms;
      }
    },
    [namesOrIndexes]
  );
  const wizardService = service ?? useWizardService();
  if (!wizardService) return undefined;
  return useSelector(wizardService, selector, (prev, next) => {
    return prev.every((s, i) => s.state === next[i].state);
  });
};

export const useForm = (name: string | number, service?: WizardInterpreter) => {
  const form = useForms([name] as string[] | number[], service);
  return Array.isArray(form) && form.length > 0
    ? form[0]
    : { state: undefined, send: undefined };
};

export const useCurrentStepIndex = (service?: WizardInterpreter) => {
  const wizardService = service ?? useWizardService();
  if (!wizardService) return undefined;
  return useSelector(wizardService, currentStepIndexSelector);
};

export const useSteps = () => {
  const { wizardService } = useContext<IWizardContext>(WizardContext);
  if (!wizardService) return undefined;
  return useSelector(wizardService, stepsSelector);
};

export const useStepValues = (
  path: string,
  nameOrIndex?: string | number | undefined,
  service?: WizardInterpreter
) => {
  const wizardService = service ?? useWizardService();
  const selector = useCallback(
    (state: WizardState) => {
      const steps = nameOrIndex
        ? getFormFromNameOrIndex(nameOrIndex, state.context.steps)
        : state.context.steps;
      if (Array.isArray(steps)) {
        return steps.map((s) => get(s.getSnapshot(), path));
      }
      return get(steps?.getSnapshot(), path);
    },
    [path, nameOrIndex]
  );
  if (!wizardService) return undefined;
  return useSelector(wizardService, selector, (prev, next) => {
    return !prev.some((value, i) => next[i] !== value);
  });
};

export const useWizardService = () => {
  const { wizardService } = useContext<IWizardContext>(WizardContext);
  return wizardService;
};

export const useValidators = <I extends AnyFormInterpreter>(service?: I) => {
  const defaultService = getDefaultService(service);
  if (!defaultService) return undefined;
  return useSelector(defaultService as I, validatorsSelector);
};

export const useTransformers = <I extends AnyFormInterpreter>(service?: I) => {
  const defaultService = getDefaultService(service);
  if (!defaultService) return undefined;
  return useSelector(defaultService as I, transformersSelector);
};

export const useFormInterpreter = (
  name: string,
  service?: WizardInterpreter
): FormInterpreter | undefined => {
  const defaultService = getDefaultService(service);
  if (!defaultService) return undefined;
  const selector = useCallback(
    (state) => {
      return state.context.steps?.find((s) => s.id === name);
    },
    [name]
  );
  return useSelector(defaultService, selector);
};
